/**
 * Live Chat outbound decoration
 *
 * Carries the Five9 chat client id to criterionticketing.com on every outbound link, so a
 * conversation started here is recognized as the same session after the hop.
 *
 * The id itself is resolved much earlier, by live-chat-id.js inlined at the top of <head> — see
 * that file for why. This script owns only the two things that need the DOM and the TagManager
 * bridge: deciding when the id is allowed onto links, and noticing engagement.
 *
 * Runs at READY because it consumes `updateOutboundDecorator`, which TagManager publishes at
 * REGISTER.
 */

const { hooks, constants } = window.sitchco;

const PARAM = 'live_chat_id';
const LAUNCHER = '#five9OpenChatButton';
const ENGAGED_EVENT = 'sitchco/live-chat/engaged';

/**
 * Duplicated from live-chat-id.js rather than imported: that file has to build to a classic,
 * import-free IIFE so it can be inlined into <head>, so there is no module to share this from.
 */
const ID_PATTERN = /^[0-9a-f]{32}$/;

function publish(clientId) {
    window.sitchco.tagManager.updateOutboundDecorator({ [PARAM]: clientId });
}

/**
 * Bring the decorator's copy of live_chat_id back in line with our own TTL.
 *
 * The decorator persists to `localStorage['outbound_params']`, which has no expiry of its own, and
 * `captureUrlParams()` re-seeds it from the URL at REGISTER — including a malformed value we
 * refused to adopt. Neither knows about our 60-minute window, so an id that has aged out would
 * otherwise keep riding links forever. We run at READY, after REGISTER, so this reconciliation is
 * the last word.
 *
 * The no-chat case clears rather than bailing. registerOutboundDecorator only prunes stored params
 * that are *not* allowed, and live_chat_id is allowed — so if the head snippet failed to load, a
 * value captured off this site's own URL would sit in the decorator's untimed store forever with
 * nothing to remove it. Note that a missing window.sitchco.liveChat is a fault, not a normal
 * state: inlineScriptAsset() emits an inline `type=module` import in dev-server mode, and an
 * inline module and its static imports still evaluate before DOMContentLoaded, so the id is
 * normally published by READY locally too. (The *head-ordering* guarantee is separately
 * production-only — the deferred module does lose to the GTM container at wp_head priority 5.)
 *
 * The purge is a beat late by design: registerOutboundDecorator() runs a synchronous document pass
 * at REGISTER using the rehydrated params, while clear() only schedules the debounced pass. For
 * ~250ms the links can still carry an expired id. Harmless — Five9 finds no session for it — and
 * not worth reaching into another module's storage key to prevent.
 */
function reconcile() {
    const chat = window.sitchco.liveChat;
    if (chat?.engaged) {
        publish(chat.clientId);
        return;
    }

    window.sitchco.tagManager.clearOutboundDecorator([PARAM]);
}

/**
 * Any unexpected failure is logged rather than left to bubble out of a lifecycle handler.
 * INIT/REGISTER/READY all run inside one DOMContentLoaded handler and @wordpress/hooks has no
 * try/catch of its own, so a throw here would take the rest of the page's boot with it. This also
 * covers TagManager having died before publishing its bridge.
 */
function guard(fn) {
    try {
        fn();
    } catch (error) {
        console.error('Live Chat decoration failed:', error);
    }
}

// Attached at module scope, not inside the READY handler: this file evaluates before
// DOMContentLoaded while READY fires inside it, so a launcher rendered by an early-firing GTM tag
// cannot be clicked before we are listening. Costs nothing, since chat is re-read on each click.
//
// Capture phase: a bubble-phase listener would lose to a stopPropagation() inside the vendor's own
// button handler.
document.addEventListener(
    'click',
    (event) => {
        if (!event.target.closest?.(LAUNCHER)) {
            return;
        }

        guard(() => window.sitchco.liveChat?.engage());
    },
    true
);

// Single publication path, so a GTM tag calling engage() from a Five9 callback decorates links
// exactly the same way the launcher click does. This is the one id-consumption path reachable with
// a value we did not resolve ourselves, so it is also the one that has to check the shape.
document.addEventListener(ENGAGED_EVENT, (event) =>
    guard(() => {
        const clientId = event.detail?.clientId;
        if (typeof clientId === 'string' && ID_PATTERN.test(clientId)) {
            publish(clientId);
        }
    })
);

hooks.addAction(constants.READY, () => guard(reconcile), 10, 'live-chat');

// A bfcache restore re-shows the page without re-running READY, so the in-memory engaged flag can
// be hours stale. refresh() settles it against storage before we act on it.
hooks.addAction(
    constants.RESTORED,
    () =>
        guard(() => {
            window.sitchco.liveChat?.refresh();
            reconcile();
        }),
    10,
    'live-chat'
);
